import { Injectable, OnDestroy } from '@angular/core';
import { find } from 'underscore';
import { GenericTreeNode } from '../../components/generic-tree/generic-tree-node';
import { AnalyticComparisonMenu, AnalyticMenu, AnalyticProfileMenu } from '../../components/main-content/menus/analytic-menu';
import { DashboardMenu } from '../../components/main-content/menus/dashboard-menu';
import { ExportMenu } from '../../components/main-content/menus/export-menu';
import { ImportMenu } from '../../components/main-content/menus/import-menu';
import { ProjectDataCustodianMenu, ProjectDataFolderMenu, ProjectDataMediaIDsMenu, ProjectDataMenu, ProjectDataTagMenu, ProjectDataBatchesMenu } from '../../components/main-content/menus/project-data-menu';
import { SearchMenu, SearchSavedSearchMenu, SearchSearchHistoryMenu } from '../../components/main-content/menus/search-menu';
import { WorkflowMenu } from '../../components/main-content/menus/workflow-menu';
import { Batch } from '../../models/batch';
import { Comparison } from '../../models/comparison';
import { Corpus } from '../../models/corpus';
import { Custodian } from '../../models/custodian';
import { DeferredPromise } from '../../models/deferred-promise';
import { DrRequestInfo } from '../../models/dr-request-info';
import { DynamicFolder } from '../../models/dynamic-folder';
import { Endpoint, EntityType, MessageType, ReviewSystem } from '../../models/enums';
import { ExportStream } from '../../models/export-stream';
import { MediaID } from '../../models/mediaid';
import { ObjectActionListType, Permissions, PermissionSet } from '../../models/permission';
import { Profile } from '../../models/profile';
import { SavedSearch } from '../../models/saved-searches';
import { SearchResult } from '../../models/search-result';
import { isNullOrUndefined, isUndefined } from '../../models/utils';
import { View, ViewType } from '../../models/view';
import { Workflow, WorkflowStep } from '../../models/workflow';
import { CommonService } from '../common/common.service';
import { RestService } from '../rest/rest.service';
import { XMLService } from '../xml/xml.service';
import * as _ from 'underscore';
import { SortByFilter } from '../../models/grid-context';
import { DiscardPileMenu } from '../../components/main-content/menus/discard-pile-menu';

@Injectable()
export class TreeDataLoaderService implements OnDestroy {
    nodes: Array<GenericTreeNode>;
    nodeMap: Map<EntityType, GenericTreeNode>;
    automationId: string;
    apiReponsesCount = 0;
    apiRequestsCount = 0;
    isLeftNavTreeMode: boolean;

    constructor(private restService: RestService, private commonService: CommonService, private xmlService: XMLService) {
    }

    createTreeNodes(): void {
        this.nodes = new Array<GenericTreeNode>();
        this.nodeMap = new Map<EntityType, GenericTreeNode>();
        this.nodeMap.set(EntityType.import, new GenericTreeNode({
            name: 'Imports', entityType: EntityType.import, automationId: this.automationId + '-imports',
            isExpandable: true, isSelectable: true,
            permissions: [{ permissionType: ObjectActionListType.CORPUS }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.corpusSetManager_listCorporaInCorpusSet,
                responseName: 'corpora',
                data: { corpusSetHandle: this.commonService.importsHandle }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new Corpus(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.import, childPadding: 0, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: item.smf ? 'fa-comments-o' : 'fa-cube', iconColor: 'color-purple-01',
                    data: item,
                    automationId: 'import-' + item.name
                });
            },
            data: { name: 'Imports', handle: this.commonService.importsHandle }
        }));
        this.nodes.push(this.nodeMap.get(EntityType.import));
        this.nodeMap.set(EntityType.export, new GenericTreeNode({
            name: 'Exports', entityType: EntityType.export, automationId: this.automationId + '-exports',
            isExpandable: true, isSelectable: false,
            isExpanded: false,
            permissions: [{ permissionType: ObjectActionListType.EXPORT_STREAM }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.workingSetManager_listExportStreams,
                responseName: 'exportStreams',
                data: {
                    handle: this.commonService.projectData.handle,
                    filters: [
                        { attribute: 'reviewProductions', value: 'false', operator: 'EQUALS' },
                        { attribute: 'reviewStream', value: 'false', operator: 'EQUALS' }
                    ]
                }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new ExportStream(data);
                return new GenericTreeNode({
                    data: item,
                    name: item.name,
                    automationId: 'export-stream-' + item.name,
                    entityType: EntityType.export,
                    customNumber: item.documentCount,
                    childPadding: 0,
                    icon: 'fa-cube',
                    iconColor: 'color-blue-01',
                    isExpandable: false,
                    hasExpanderPadding: true
                });
            }
        }));
        this.nodes.push(this.nodeMap.get(EntityType.export));
        const projectDataNode = new GenericTreeNode({
            name: 'Project Data', automationId: this.automationId + '-project-data', entityType: EntityType.projectData,
            permissions: [{ permissionType: ObjectActionListType.ECA_CASE_DATA }],
            isExpanded: true, isExpandable: true,
            isSelectable: true,
            data: this.commonService.projectData,
        })
        this.nodeMap.set(EntityType.projectData, projectDataNode);
        this.nodeMap.set(EntityType.custodian, new GenericTreeNode({
            name: 'Custodians', entityType: EntityType.custodian, automationId: this.automationId + '-project-data-custodians',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: projectDataNode,
            permissions: [{ permissionType: ObjectActionListType.ECA_CUSTODIAN }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.ecaManager_listCustodians,
                responseName: 'custodians',
                data: { projectHandle: this.commonService.currentProject.handle }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new Custodian(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.custodian, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: item.name === 'Unassigned' ? 'fa-user-circle' : 'fa-user-circle-o', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                    data: item,
                    automationId: 'custodian-' + item.name
                });
            },
        }));
        this.nodeMap.set(EntityType.mediaID, new GenericTreeNode({
            name: 'MediaIDs', entityType: EntityType.mediaID, automationId: this.automationId + '-project-data-mediaids',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: projectDataNode,
            permissions: [{ permissionType: ObjectActionListType.ECA_MEDIAID }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.ecaManager_listMediaids,
                responseName: 'mediaids',
                data: { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: true }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new MediaID(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.mediaID, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: 'fa-laptop', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                    data: item,
                    automationId: 'mediaid-' + item.name
                });
            },
        }));
        this.nodeMap.set(EntityType.batch, new GenericTreeNode({
            name: 'Batches', entityType: EntityType.batch, automationId: this.automationId + '-project-data-batches',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: projectDataNode,
            permissions: [{ permissionType: ObjectActionListType.ECA_BATCH }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.ecaManager_listBatches,
                responseName: 'batches',
                data: { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: true }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new Batch(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.batch, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: 'fa-archive', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                    data: item,
                    automationId: 'batch-' + item.name
                });
            },
        }));
        this.nodeMap.set(EntityType.tagView, new GenericTreeNode({
            name: 'Tags', entityType: EntityType.tagView, automationId: this.automationId + '-project-data-tags',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: projectDataNode,
            permissions: [{ permissionType: ObjectActionListType.PRJ_TAGS }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.workingSetManager_listWorkingSetViews,
                responseName: 'views',
                data: {
                    filters: [{ attribute: 'viewType', value: 'TAG', operator: 'EQUALS' }],
                    workingSetHandle: this.commonService.projectData.handle
                },
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new View(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.tagView, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: 'fa-tag', iconColor: item.attributes && item.attributes.length > 0 ? item.attributes.filter(a => a.name === 'displayValue')[0]?.value : null,
                    data: item,
                    automationId: 'tag-' + item.name
                });
            }
        }));
        this.nodeMap.set(EntityType.folder, new GenericTreeNode({
            name: 'Folders', entityType: EntityType.folder, automationId: this.automationId + '-project-data-folders',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: projectDataNode,
            permissions: [{ permissionType: ObjectActionListType.ECA_FOLDER }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listDynamicFolders,
                responseName: 'dynamicFolders',
                data: { projectHandle: this.commonService.currentProject.handle }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new DynamicFolder(data);
                return new GenericTreeNode({
                    name: item.name, entityType: EntityType.folder, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                    icon: 'fa-folder', iconColor: item.color,
                    data: item,
                    automationId: 'folder-' + item.name
                });
            },
        }));
        projectDataNode.children.push(this.nodeMap.get(EntityType.custodian), this.nodeMap.get(EntityType.mediaID), this.nodeMap.get(EntityType.batch), this.nodeMap.get(EntityType.tagView), this.nodeMap.get(EntityType.folder));
        this.nodes.push(projectDataNode);
        const searchesNode = new GenericTreeNode({
            name: 'Searches', automationId: this.automationId + '-searches',
            isExpandable: true, isSelectable: false, isExpanded: true
        });
        this.nodeMap.set(EntityType.searchHistory, searchesNode);
        this.nodeMap.set(EntityType.savedSearch, new GenericTreeNode({
            name: 'Saved Searches', entityType: EntityType.savedSearch, automationId: this.automationId + '-searches-saved-searches',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: searchesNode,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.SAVED_SEARCH_QUERY })],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listSavedSearches,
                responseName: 'activeRules',
                data: {
                    projectHandle: this.commonService.currentProject.handle,
                    startIndex: 0,
                    count: 10
                },
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new SavedSearch(data);
                return new GenericTreeNode({
                    name: item.name,
                    entityType: EntityType.savedSearch,
                    childPadding: 7.5,
                    hasExpanderPadding: true,
                    icon: 'fa-search',
                    iconColor: 'color-tan-07',
                    customNumber: item.view.documentCount,
                    data: item,
                    automationId: 'saved-search-' + item.handle
                });
            }
        }));

        this.nodeMap.set(EntityType.searchResult, new GenericTreeNode({
            name: 'Search History', entityType: EntityType.searchResult, automationId: this.automationId + '-searches-search-history',
            isExpandable: true, childPadding: 0, isSelectable: false, parent: searchesNode,
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listSearchHistory,
                responseName: 'views',
                data: {
                    projectHandle: this.commonService.currentProject.handle,
                    startIndex: 0,
                    count: 10
                }
            }),
            createChild: (data: any): GenericTreeNode => {
                const item = new SearchResult(data);
                return new GenericTreeNode({
                    name: item.name,
                    entityType: EntityType.searchResult,
                    childPadding: 7.5,
                    hasExpanderPadding: true,
                    customNumber: item.documentCount,
                    icon: (item.task !== 'SEARCH_FROM_BULK_SEARCH' && item.task !== 'BULK_SEARCH') ? 'fa-search' : null,
                    iconColor: 'color-tan-07',
                    iconStringContent: (item.task === 'SEARCH_FROM_BULK_SEARCH' || item.task === 'BULK_SEARCH') ? 'B' : null,
                    data: item,
                    automationId: 'search-history-' + item.handle
                });
            },
        }));
        searchesNode.children.push(this.nodeMap.get(EntityType.savedSearch), this.nodeMap.get(EntityType.searchResult));
        this.nodes.push(searchesNode);
        this.nodeMap.set(EntityType.workFlow, new GenericTreeNode({
            name: 'Workflows', entityType: EntityType.workFlow, automationId: this.automationId + '-workflows',
            isExpandable: true, isSelectable: false,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW })],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.workFlowManager_getWorkFlows,
                responseName: 'workFlows',
                data: {
                    projectHandle: this.commonService.currentProject.handle,
                    startIndex: 0
                }
            }),
            createChild: (data: any): GenericTreeNode => {
                const wfItem = new Workflow(data);
                return new GenericTreeNode({
                    name: wfItem.name,
                    automationId: 'workflow-' + wfItem.name,
                    entityType: EntityType.workFlow,
                    isSelectable: true,
                    childPadding: 0,
                    hasExpanderPadding: true,
                    icon: this.getWorkflowIconByStatus(wfItem),
                    iconColor: 'color-green-07',
                    data: wfItem,
                    isExpandable: true,
                    isExpandedOnSelect: true,
                    children: [], // children are set with setWorkflowChildren()
                });
            }
        }));
        this.nodes.push(this.nodeMap.get(EntityType.workFlow));
        const analyticMenu = new GenericTreeNode({
            name: 'Analytics',
            automationId: this.automationId + '-analytics',
            isExpanded: true,
            isExpandable: true,
            isExpandedOnSelect: true,
            isSelectable: false,
            permissions: [{ permissionType: ObjectActionListType.COMPARISONS }, { permissionType: ObjectActionListType.PROFILES }], atLeastOne: true,
        });
        this.nodeMap.set(EntityType.analytics, analyticMenu);
        this.nodeMap.set(EntityType.comparison, new GenericTreeNode({
            name: 'Comparisons',
            entityType: EntityType.comparison,
            automationId: this.automationId + '-analytics-comparisons',
            isExpandable: true,
            isSelectable: false,
            isExpandedOnSelect: true,
            childPadding: 0,
            permissions: [{ permissionType: ObjectActionListType.COMPARISONS }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listComparisons,
                responseName: 'comparisons',
                data: { startIndex: 0, count: 0, projectHandle: this.commonService.currentProject.handle }
            }),

            createChild: (data: any, handler?: any): GenericTreeNode => {
                const item = new Comparison(data);
                return new GenericTreeNode({
                    name: item.name,
                    entityType: EntityType.comparison,
                    icon: 'fa-exchange',
                    iconColor: 'color-purple-02',
                    hasExpanderPadding: true,
                    isExpandable: false,
                    isExpandedOnSelect: false,
                    data: item,
                    childPadding: 7.5,
                    automationId: 'comparison-' + item.name
                }, handler);
            }
        }));
        this.nodeMap.set(EntityType.profile, new GenericTreeNode({
            name: 'Synthetic Documents',
            entityType: EntityType.profile,
            automationId: this.automationId + '-analytics-synthetic-documents',
            isExpandable: true,
            isExpandedOnSelect: true,
            isSelectable: false,
            childPadding: 0,
            permissions: [{ permissionType: ObjectActionListType.PROFILES }],
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listProfiles,
                responseName: 'profiles',
                data: { startIndex: 0, count: 0, handle: this.commonService.currentProject.handle }
            }),
            createChild: (data: any, handler?: any): GenericTreeNode => {
                const item = new Profile(data);
                return new GenericTreeNode({
                    name: item.name,
                    entityType: EntityType.profileHistory,
                    icon: 'fa-align-left',
                    iconColor: 'color-purple-02',
                    isExpandable: false,
                    isExpandedOnSelect: false,
                    hasExpanderPadding: true,
                    data: item,
                    childPadding: 7.5,
                    automationId: 'synthetic-document-' + item.name,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.profileManager_listProfileHistories,
                        responseName: 'profileHistory',
                        data: { handle: item.handle, count: 50, startIndex: 0 }
                    }),
                }, handler);
            }
        }));
        analyticMenu.children.push(this.nodeMap.get(EntityType.comparison), this.nodeMap.get(EntityType.profile));
        this.nodes.push(analyticMenu);
        const discardPileNode = new GenericTreeNode({
            name: 'Discard Pile', automationId: this.automationId + '-discard-pile', entityType: EntityType.discardPile,
            isExpandable: false, customClasses: 'node-left-margin',
            isSelectable: true,
            icon: 'fa-recycle', iconColor: 'color-grey-10',
            data: this.commonService.discardPile,
        })
        this.nodeMap.set(EntityType.discardPile, discardPileNode);
        this.nodes.push(discardPileNode);
    }

    public loadData(isLoadFromLeftNavTree = false): Promise<any> {
        const promiseToReturn = new DeferredPromise<any>();
        this.isLeftNavTreeMode = isLoadFromLeftNavTree;
        Array.from(this.nodeMap.values()).forEach(node => {
            let promiseResponse = null;
            if (!node.permissions || (node.permissions && Permissions.checkUserPermissions(node.permissions))) {
                promiseResponse = this.loadNodeData(node);
            }
            if (promiseResponse) {
                promiseResponse.then(() => {
                    if (this.apiRequestsCount === this.apiReponsesCount) {
                        this.apiRequestsCount = this.apiReponsesCount = 0;
                        promiseToReturn.resolve(true);
                    } else {
                        this.commonService.sendMessage(MessageType.refreshTreeNodesWithLoadingInProgress);
                    }
                })
            }
        });
        if (this.apiRequestsCount > this.apiReponsesCount) {
            return promiseToReturn.promise;
        }
    }

    createNavTreeNodes(): Array<GenericTreeNode> {
        this.nodes = new Array<GenericTreeNode>(
            ImportMenu,
            ExportMenu,
            ProjectDataMenu,
            SearchMenu,
            WorkflowMenu,
            AnalyticMenu,
            DiscardPileMenu
        );
        this.nodeMap = new Map<EntityType, GenericTreeNode>();
        this.nodeMap.set(EntityType.import, ImportMenu);
        this.nodeMap.set(EntityType.export, ExportMenu);
        this.nodeMap.set(EntityType.projectData, ProjectDataMenu);
        this.nodeMap.set(EntityType.custodian, ProjectDataCustodianMenu);
        this.nodeMap.set(EntityType.mediaID, ProjectDataMediaIDsMenu);
        this.nodeMap.set(EntityType.batch, ProjectDataBatchesMenu);
        this.nodeMap.set(EntityType.tagView, ProjectDataTagMenu);
        this.nodeMap.set(EntityType.folder, ProjectDataFolderMenu);
        this.nodeMap.set(EntityType.searchHistory, SearchMenu);
        this.nodeMap.set(EntityType.savedSearch, SearchSavedSearchMenu);
        this.nodeMap.set(EntityType.searchResult, SearchSearchHistoryMenu);
        this.nodeMap.set(EntityType.workFlow, WorkflowMenu);
        this.nodeMap.set(EntityType.analytics, AnalyticMenu);
        this.nodeMap.set(EntityType.comparison, AnalyticComparisonMenu);
        this.nodeMap.set(EntityType.profile, AnalyticProfileMenu);
        this.nodeMap.set(EntityType.discardPile, DiscardPileMenu);
        return this.nodes;
    }

    public resetSearchCount() {
        this.nodeMap.get(EntityType.searchResult).requestInfo.data.count = 10;
    }

    public loadNodeData = (node: GenericTreeNode, apiCountingEnabled = true): Promise<any> => {
        if (node && !node.isLoading && !node.isDisabled && node.isExpandable && node.canLoadData()) {
            node.isLoading = true;
            if (apiCountingEnabled) {
                this.apiRequestsCount++;
            }
            if (node?.data && node.entityType == EntityType.export) {
                node.requestInfo.data.count = 0; 
                node.requestInfo.data.sortByFilter = node.parent.requestInfo.data.sortByFilter === SortByFilter.DATE_CREATED ? SortByFilter.DATE_CREATED : SortByFilter.NAME;
                node.requestInfo.data.descending = node.requestInfo.data.sortByFilter === SortByFilter.NAME ? false: true;
            }
            return this.restService.post(node.requestInfo.endpoint, node.requestInfo.data).then(response => {
                if (response) {
                    const responseList = response[node.requestInfo.responseName];
                    switch (node.entityType) {
                        case EntityType.searchResult: {
                            this.setSearchResultsData(responseList, response.totalCount);
                            break;
                        }
                        case EntityType.import: {
                            this.setImportsResultsData(responseList, response.totalCount);
                            break;
                        }
                        case EntityType.export: {
                            if (!(node?.data instanceof ExportStream)) {
                                node.isReefReviewProject = this.commonService?.currentProject?.reviewProjectId !== 0 && this.commonService?.currentProject?.reviewSystem === ReviewSystem.REEF_REVIEW;
                                node.isRelativityProject = this.commonService?.currentProject?.reviewSystem === ReviewSystem.RELATIVITY;
                            }

                            const isExportVolume = node?.data instanceof ExportStream ? true : false;
                            this.setExportStreamsResultsData(responseList, response.totalCount, node.name, isExportVolume);
                            break;
                        }
                        case EntityType.workFlow: {
                            if (this.nodeMap) {
                                this.nodeMap.get(EntityType.workFlow).setChildren(responseList);

                                if (!this.isLeftNavTreeMode) {
                                    this.setWorkflowChildren();
                                }
                            }
                            break;
                        }
                        default: {
                            node.setChildren(responseList);
                        }
                    }
                }
                node.isLoading = false;
                if (apiCountingEnabled) {
                    this.apiReponsesCount++;
                }
            }, () => {
                node.isLoading = false;
                if (apiCountingEnabled) {
                    this.apiReponsesCount++;
                }
            });
        } else {
            return null;
        }
    }

    getWorkflowIconByStatus(workflowNodeData: any): string {
        let isRunning = false;
        if (workflowNodeData?.attributes) {
            const runningAttrib = find(workflowNodeData.attributes, (attribute) => attribute.name === 'isRunning');
            isRunning = runningAttrib?.value === 'true';
        }
        return isRunning ? 'fa-spinner fa-spin' : 'fa-list-ol';
    }

    setWorkflowChildren(parent?: GenericTreeNode): void {
        const local = !parent;
        if (!parent) {
            parent = this.nodeMap.get(EntityType.workFlow);
        }
        parent?.children.forEach(wfNode => {
            if (wfNode?.data?.queryItems?.items) {
                wfNode.icon = this.getWorkflowIconByStatus(wfNode.data);
                wfNode?.data?.queryItems?.items.forEach(queryItem => {
                    const stepRun = !isNullOrUndefined(queryItem.responseHandle);
                    queryItem.handle = stepRun ? queryItem.responseHandle : queryItem.handle;
                    const stepItem = new WorkflowStep(queryItem);
                    const viewTypeAttr = find(stepItem.attributes, attr => attr.name === 'steptype');
                    const noOfResults = find(stepItem.attributes, attr => attr.name === 'noOfResults');

                    if (viewTypeAttr) {
                        stepItem.viewType = viewTypeAttr.value;
                    }
                    const name = stepItem.viewType?.toString() + ': ' + (this.xmlService.xmlToQueryConverter(stepItem.query));
                    const stepNode = new GenericTreeNode({
                        name: name,
                        entityType: EntityType.searchResult,
                        iconStringContent: stepItem.orderOfExecution ? stepItem.orderOfExecution.toString(10) : '1',
                        isExpandable: false,
                        hasExpanderPadding: true,
                        data: stepItem,
                        customNumber: noOfResults?.value,
                        isDisabled: !stepRun,
                        automationId: this.automationId + 'workflow-step-' + stepItem.handle,
                        parent: wfNode,
                    });
                    wfNode.children.push(stepNode)
                });
            }
        });
        if (this.nodes && local) {
            const workflowsNodeIdx = this.nodes.findIndex(n => n.name === 'Workflows');
            if (workflowsNodeIdx !== -1) {
                this.nodes[workflowsNodeIdx] = this.nodeMap.get(EntityType.workFlow);
            }
        }
    }

    disableContextMenuOptionsByParsingVersion(item: any, projectParsingVersion: string): void {
        item.enabled = projectParsingVersion === 'V2';
        item.tooltipFunction = () => projectParsingVersion === 'V1' ? 'You can no longer perform this operation for a Project using Parsing Library Version V1.' : '';
        item.tooltipAlwaysOn = projectParsingVersion === 'V1';
    }

    setSearchResultsData(searchHistoryList: any, totalCount: number, filterParam = null): void {
        if (searchHistoryList) {
            const remainingSearchesCount = Math.min(10, totalCount - searchHistoryList.length);
            const standardMoreButtonName = remainingSearchesCount > 0 ? `${remainingSearchesCount} more ...` : '';
            if (this.isLeftNavTreeMode) {
                this.commonService.moreButtonOriginalLabel = standardMoreButtonName;
            }
            if (remainingSearchesCount > 0 && !filterParam) {
                searchHistoryList.push(new GenericTreeNode({
                    name: !this.isLeftNavTreeMode ? standardMoreButtonName : (this.commonService.hideArchivedItems ? 'more ...' : this.commonService.moreButtonOriginalLabel),
                    hasExpanderPadding: true,
                    childPadding: 20,
                    entityType: EntityType.searchResult,
                    isSelectable: false,
                    isButton: true,
                    getMoreOnSelectedNodeCallback: true,
                    automationId: 'get-more-searches'
                }));
            }
            if (this.nodeMap) {
                this.nodeMap.get(EntityType.searchResult).setChildren(searchHistoryList);
            }
            if (this.nodes) {
                const searchHistoryNodeIdx = this.nodes.findIndex(n => n.name === 'Searches');
                if (searchHistoryNodeIdx !== -1 && this.nodes[searchHistoryNodeIdx].children?.length === 2 && this.nodes[searchHistoryNodeIdx].children[1]?.children.length < searchHistoryList.length - 1) {
                    this.nodes[searchHistoryNodeIdx].children[1] = this.nodeMap.get(EntityType.searchResult);
                    this.nodes[searchHistoryNodeIdx].isExpanded = true;
                    this.nodes[searchHistoryNodeIdx].children[1].isExpanded = true;
                }

                if (searchHistoryNodeIdx && searchHistoryNodeIdx !== -1 && this.nodes[searchHistoryNodeIdx]?.children[1]) {
                    const searchNode = this.nodes[searchHistoryNodeIdx].children[1];
                    const projectParsingVersion = this.commonService?.currentProject?.attributes?.find(a => a.name === 'parsingVersion')?.value;
                    searchNode.children.forEach(item => {
                        const itemReprocessCM = item?.contextMenuOptions?.find(o => o.name === 'Reprocess ...');
                        const itemLoadFromExternalCM = item?.contextMenuOptions?.find(o => o.name === 'Load from External Area ...');
                        const itemCreateDsFromSearchResultCM = item?.contextMenuOptions?.find(o => o.name === 'Create Data Set from Search Results ...');
                        const itemExtractOffice365DataCM = item?.contextMenuOptions?.find(o => o.name === 'Extract Office 365 Data ...');
                        if (itemReprocessCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT ||
                            (item.data as SearchResult)?.viewType === ViewType.WORKING_SET_RESULT) && projectParsingVersion === 'V1') {
                            this.disableContextMenuOptionsByParsingVersion(itemReprocessCM, projectParsingVersion);
                        } else if (itemReprocessCM) {
                            itemReprocessCM.enabled = (item.data instanceof WorkflowStep) ? (item.data as WorkflowStep).numberResults > 0 : ((item.data as SearchResult).documentCount > 0 && (item.data.viewType != null || !isUndefined(_.findWhere((item.data as SearchResult).attributes, { name: 'viewtype' }))));
                            itemReprocessCM.tooltipAlwaysOn = false;
                            itemReprocessCM.tooltipFunction = (item: GenericTreeNode) => '';
                        }
                        if (itemLoadFromExternalCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                            this.disableContextMenuOptionsByParsingVersion(itemLoadFromExternalCM, projectParsingVersion);
                        } else if (itemLoadFromExternalCM) {
                            itemLoadFromExternalCM.enabled = (item.data as SearchResult).documentCount > 0;
                            itemLoadFromExternalCM.tooltipAlwaysOn = false;
                            itemLoadFromExternalCM.tooltipFunction = (item: GenericTreeNode) => '';
                        }
                        if (itemCreateDsFromSearchResultCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                            this.disableContextMenuOptionsByParsingVersion(itemCreateDsFromSearchResultCM, projectParsingVersion);
                        } else if (itemCreateDsFromSearchResultCM) {
                            itemCreateDsFromSearchResultCM.enabled = (item.data as SearchResult).documentCount > 0;
                            itemCreateDsFromSearchResultCM.tooltipAlwaysOn = false;
                            itemCreateDsFromSearchResultCM.tooltipFunction = (item: GenericTreeNode) => '';
                        }
                        if (itemExtractOffice365DataCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && (
                            (item.data as SearchResult).name.toLowerCase().includes('modern attachment retrieve warning') ||
                            (item.data as SearchResult).name.toLowerCase().includes('missing channel name warning')
                        )) {
                            itemExtractOffice365DataCM.enabled = this.commonService.mainContent.office365Connectors.length > 0;
                        }
                    });
                }
            }
        }
    }

    setImportsResultsData(DataAreasList: any, totalCount: number): void {
        if (DataAreasList) {
            const remainingDataAreasCount = totalCount - DataAreasList.length;
            const standardMoreButtonName = remainingDataAreasCount > 0 ? `${remainingDataAreasCount} more ...` : '';
            if (this.isLeftNavTreeMode) {
                this.commonService.moreDataAreasButtonOriginalLabel = standardMoreButtonName;
            }
            if (remainingDataAreasCount > 0) {
                DataAreasList.push(new GenericTreeNode({
                    name: standardMoreButtonName,
                    hasExpanderPadding: true,
                    childPadding: 20,
                    entityType: EntityType.import,
                    isSelectable: false,
                    isButton: true,
                    getMoreOnSelectedNodeCallback: true,
                    automationId: 'get-more-dataareas'
                }));
            }
            if (this.nodeMap) {
                this.nodeMap.get(EntityType.import).setChildren(DataAreasList);
            }
        }
    }

    setExportStreamsResultsData(ExportStreamsList: any, totalCount: number, nodeName: string, isExportVolume: boolean): void {
        if (ExportStreamsList) {
            const remainingExportStreamsCount = totalCount - ExportStreamsList.length;
            const standardMoreButtonName = remainingExportStreamsCount > 0 ? `${remainingExportStreamsCount} more ...` : '';
            if (this.isLeftNavTreeMode) {
                this.commonService.moreDataAreasButtonOriginalLabel = standardMoreButtonName;
            }
            if (remainingExportStreamsCount > 0) {
                ExportStreamsList.push(new GenericTreeNode({
                    name: standardMoreButtonName,
                    hasExpanderPadding: true,
                    childPadding: 20,
                    entityType: EntityType.export,
                    isSelectable: false,
                    isButton: true,
                    getMoreOnSelectedNodeCallback: true,
                    automationId: 'get-more-exportstreams'
                }));
            }
            if (this.nodeMap && !isExportVolume) {
                this.nodeMap.get(EntityType.export).setChildren(ExportStreamsList);
            } else {
                const exportStreamNode = this.nodeMap.get(EntityType.export);
                if (exportStreamNode?.children?.length > 0) {
                    const exportStream = exportStreamNode.children.filter(exportVolume => exportVolume.name === nodeName);
                    if (exportStream && exportStream.length > 0) {
                        exportStream[0].setChildren(ExportStreamsList);
                    }
                }
            }
        }
    }

    loadMoreResults(resultsCount: number, parentNode: GenericTreeNode, isMoreClickFromLeftNavTree = false): Promise<any> {
        this.isLeftNavTreeMode = isMoreClickFromLeftNavTree;
        if (parentNode?.requestInfo?.data) {
            if (parentNode?.isSortable || (parentNode?.entityType === EntityType.export && parentNode?.parent?.isSortable)) {
                // add sortable request info 
                parentNode.requestInfo.data.sortByFilter = parentNode.requestInfo.data.sortByFilter === SortByFilter.DATE_CREATED ? SortByFilter.DATE_CREATED : SortByFilter.NAME ;
                parentNode.requestInfo.data.descending = parentNode.requestInfo.data.sortByFilter === SortByFilter.NAME ? false: true;
            }
            parentNode.requestInfo.data.count = resultsCount;
            return this.loadNodeData(parentNode, false);
        }
        return null;
    }

    private clearTreeNodeData(item: GenericTreeNode) {
        if (item) {
            delete item.data;
            delete item.customNumber;
            if (item.navigationInfo) {
                item.navigationInfo.selectedTab = null;
            }
            item.level = 0;
            if (item.children.length > 0) {
                item.children.forEach(child => {
                    this.clearTreeNodeData(child);
                });
                if (item !== ProjectDataMenu && item !== SearchMenu && item !== AnalyticMenu) {
                    item.children = [];
                }
                if (item === SearchSearchHistoryMenu) {
                    item.requestInfo.data.count = 10;
                }
            }
        }
    }

    ngOnDestroy(): void {
        if (this.nodes) {
            this.nodes.forEach(child => {
                this.clearTreeNodeData(child);
            });
            this.nodes = null;
        }
        if (this.nodeMap) {
            this.nodeMap.clear();
            this.nodeMap = null;
        }
    }
}
